// The morning summary on the admin home: everything outstanding across the firms, split
// into Urgent and Not urgent, each line opening the page where the work is done.
//
// Nothing here is new data. It reads what the tools already keep - the admin inbox,
// certifications, Aid Planner matters, the VALS and EAS diaries, new file emails, emailed
// fee slips, Recover from aid, and the LACW and EAS weekly billing - and says what is
// waiting. The rules were agreed with Emma on 21 Oct 2026:
//   - a grant or extension is urgent when the Aid Planner or diary says to apply now;
//     one due in the next two weeks is not urgent yet;
//   - an inbox request nobody has started is urgent after 2 days, a certification after 1;
//   - a weekly billing week turns urgent a full week after it ended;
//   - long backlogs (old file emails, emailed items never sorted) are one line each, so
//     they do not bury today's work.
// An urgent line also names the items behind it (`targets`, with `focus`, the kind of item):
// the summary opens one straight away, or lists several to choose from (Emma, 1 Nov 2026).
//
// Pure, so it is tested without a portal session: the page fetches, this decides.

import { describeTiming, formatReminderDate, plannerReminders, diaryReminders } from "./grantReminders.js";
import { openRequisitions } from "./atlasRequisitions.js";
import { buildRecoupRows, normaliseRecoupData } from "./budgetRecoup.js";
import { normaliseDisbursementData } from "./disbursements.js";
import { emailClientName } from "./emailIntake.js";
import { requestTypeLabel } from "./portalRequests.js";
import { pickLatestWeek, readWeekArchive } from "./valsDiaryWeeks.js";

export const FIRM_LABELS = { lacw: "LACW", vals: "VALS", eas: "EAS", rga: "RGA", admin: "Admin" };
export const SUMMARY_FIRMS = ["lacw", "vals", "eas", "rga"];

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const REQUEST_URGENT_AFTER_DAYS = 2;
export const CERTIFICATION_URGENT_AFTER_DAYS = 1;
export const GRANT_SOON_DAYS = 14;
export const FILE_EMAIL_RECENT_DAYS = 30;
export const INTAKE_RECENT_DAYS = 7;
// A billing week turns urgent this many days after its Friday: 12-16 Oct on Sat 24 Oct.
export const BILLING_URGENT_AFTER_DAYS = 8;
// Unstarted LACW weeks listed at most; older gaps are not this summary's business.
export const MAX_BILLING_WEEKS = 4;
// Urgent grant lines are listed one matter per line; past this they are summed up.
const MAX_LISTED = 5;

const firmLabel = (brand) => FIRM_LABELS[String(brand || "").toLowerCase()] || String(brand || "").toUpperCase();
const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

// Calendar days as UTC midnights, so daylight saving never moves a day.
function dayOf(date) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDay(value) {
  if (!value) return null;
  const text = String(value).trim();
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) return Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return null;
}

// Whole days from a timestamp to today (0 = today), by the local calendar.
function daysSince(value, today) {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return null;
  return Math.round((dayOf(today) - dayOf(new Date(time))) / DAY_MS);
}

function mondayOf(day) {
  const weekday = new Date(day).getUTCDay();
  return day - ((weekday + 6) % 7) * DAY_MS;
}

const shortDate = (day) => {
  const date = new Date(day);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
};

// "12–16 Oct", or "29 Sep – 3 Oct" across a month.
export function weekRangeLabel(monday) {
  const friday = monday + 4 * DAY_MS;
  const start = new Date(monday);
  const end = new Date(friday);
  return start.getUTCMonth() === end.getUTCMonth()
    ? `${start.getUTCDate()}–${end.getUTCDate()} ${MONTHS[end.getUTCMonth()]}`
    : `${shortDate(monday)} – ${shortDate(friday)}`;
}

const billingWeekUrgent = (monday, today) => dayOf(today) >= monday + (4 + BILLING_URGENT_AFTER_DAYS) * DAY_MS;

function item(urgent, fields) {
  return { urgent, detail: "", ...fields };
}

// One item an urgent line is about, as the summary lists it.
function target(id, label, detail = "") {
  return { id: String(id), label: String(label || "").trim() || "Unnamed", detail: String(detail || "").trim() };
}

// "16 Oct" for a bare day as written, or for a stored time by the local calendar: 9am on
// 16 Oct in Melbourne is stored as the 15th in London.
const shortWhen = (value) => {
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$|^\d{1,2}\/\d{1,2}\/\d{4}$/.test(text)) return shortDate(parseDay(text));
  const time = new Date(text);
  return text && Number.isFinite(time.getTime()) ? shortDate(dayOf(time)) : "";
};
const money = (value) => `$${(Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, "")) || 0).toFixed(2)}`;

// ---- Admin inbox -------------------------------------------------------------------------

export function requestItems(requests = [], today = new Date()) {
  const items = [];
  const byFirm = new Map();
  for (const request of Array.isArray(requests) ? requests : []) {
    if (!["received", "in_progress", "blocked"].includes(request?.status)) continue;
    const brand = String(request.brand || "admin").toLowerCase();
    const group = byFirm.get(brand) || { waiting: [], fresh: [], started: [] };
    if (request.status === "received") {
      const age = daysSince(request.submitted_at || request.created_at, today);
      const overdue = request.due_at && new Date(request.due_at).getTime() < today.getTime();
      if (request.priority === "urgent" || overdue || (age !== null && age > REQUEST_URGENT_AFTER_DAYS)) {
        group.waiting.push(request);
      } else {
        group.fresh.push(request);
      }
    } else {
      group.started.push(request);
    }
    byFirm.set(brand, group);
  }
  for (const [brand, group] of byFirm) {
    const firm = firmLabel(brand);
    if (group.waiting.length) {
      const oldest = group.waiting
        .map((request) => request.submitted_at || request.created_at)
        .filter(Boolean)
        .sort()[0];
      items.push(item(true, {
        id: `inbox-waiting-${brand}`,
        firm,
        text: `${plural(group.waiting.length, "inbox request")} not started for more than ${REQUEST_URGENT_AFTER_DAYS} days`,
        detail: oldest ? `Oldest sent ${shortWhen(oldest)}` : "",
        tab: "inbox",
        page: "Admin inbox",
        brand,
        focus: "request",
        targets: group.waiting.map((request) => target(
          request.id,
          [requestTypeLabel(request.request_type), request.client_name || request.title].filter(Boolean).join(": "),
          `Sent ${shortWhen(request.submitted_at || request.created_at)}`,
        )),
      }));
    }
    if (group.fresh.length) {
      items.push(item(false, {
        id: `inbox-new-${brand}`,
        firm,
        text: `${plural(group.fresh.length, "new inbox request")} not started yet`,
        tab: "inbox",
        page: "Admin inbox",
      }));
    }
    if (group.started.length) {
      items.push(item(false, {
        id: `inbox-started-${brand}`,
        firm,
        text: `${plural(group.started.length, "inbox request")} you've started`,
        tab: "inbox",
        page: "Admin inbox",
      }));
    }
  }
  return items;
}

// ---- Certifications ---------------------------------------------------------------------

export function certificationItems(byBrand = {}, today = new Date()) {
  const items = [];
  for (const [brand, rows] of Object.entries(byBrand || {})) {
    const pending = (Array.isArray(rows) ? rows : []).filter((row) => row?.status === "pending");
    if (!pending.length) continue;
    const late = pending.filter((row) => (daysSince(row.submitted_at || row.created_at, today) ?? 0) > CERTIFICATION_URGENT_AFTER_DAYS);
    const firm = firmLabel(brand);
    if (late.length) {
      items.push(item(true, {
        id: `cert-late-${brand}`,
        firm,
        text: `${plural(late.length, "application")} waiting more than a day to be certified`,
        tab: "certifications",
        page: "Certifications",
        brand,
        focus: "certification",
        targets: late.map((row) => target(row.id, row.client_name || "Legal aid application", `Sent ${shortWhen(row.submitted_at || row.created_at)}`)),
      }));
    }
    if (pending.length > late.length) {
      items.push(item(false, {
        id: `cert-new-${brand}`,
        firm,
        text: `${plural(pending.length - late.length, "application")} waiting to be certified`,
        tab: "certifications",
        page: "Certifications",
      }));
    }
  }
  return items;
}

// ---- Grants and extensions --------------------------------------------------------------

const APPLY_NOW = new Set(["overdue", "urgent", "due"]);

function soon(reminder, today) {
  if (reminder.urgency !== "upcoming") return false;
  const applyBy = parseDay(reminder.applyBy);
  return applyBy !== null && applyBy - dayOf(today) <= GRANT_SOON_DAYS * DAY_MS;
}

// One firm's reminders, from the Aid Planner or a diary, as summary lines.
export function reminderItems(reminders = [], { brand, tab, page, source, today = new Date() }) {
  const firm = firmLabel(brand);
  // The Aid Planner opens a matter; a diary, the appearance.
  const focus = source === "planner" ? "matter" : "diary-entry";
  const targetOf = (reminder) => target(reminder.entryId, `${reminder.label}: ${reminder.clientName}`, describeTiming(reminder));
  const now = reminders.filter((reminder) => APPLY_NOW.has(reminder.urgency));
  const upcoming = reminders.filter((reminder) => soon(reminder, today));
  const items = now.slice(0, MAX_LISTED).map((reminder) => item(true, {
    id: `grant-${source}-${brand}-${reminder.id}`,
    firm,
    text: `${reminder.label}: ${reminder.clientName}`,
    detail: describeTiming(reminder),
    tab,
    page,
    brand,
    focus,
    targets: reminder.entryId ? [targetOf(reminder)] : [],
  }));
  if (now.length > MAX_LISTED) {
    items.push(item(true, {
      id: `grant-${source}-${brand}-more`,
      firm,
      text: `${plural(now.length - MAX_LISTED, "more grant or extension", "more grants or extensions")} to apply for now`,
      tab,
      page,
      brand,
      focus,
      targets: now.slice(MAX_LISTED).filter((reminder) => reminder.entryId).map(targetOf),
    }));
  }
  if (upcoming.length) {
    const first = upcoming.map((reminder) => reminder.applyBy).sort()[0];
    items.push(item(false, {
      id: `grant-${source}-${brand}-soon`,
      firm,
      text: `${plural(upcoming.length, "extension or new grant", "extensions or new grants")} due in the next 2 weeks`,
      detail: first ? `First apply-by ${formatReminderDate(first)}` : "",
      tab,
      page,
    }));
  }
  return items;
}

export const PLANNER_TABS = { lacw: "lacw-aid-planner" };

export function plannerItems(matterRows = [], { today = new Date(), availabilityFor } = {}) {
  const byBrand = new Map();
  for (const row of Array.isArray(matterRows) ? matterRows : []) {
    if (!row?.planner) continue;
    const brand = String(row.brand || "lacw").toLowerCase();
    byBrand.set(brand, [...(byBrand.get(brand) || []), row.planner]);
  }
  const items = [];
  for (const [brand, planners] of byBrand) {
    // Only LACW's Aid Planner is on the admin portal; any other firm's matters open there too.
    const tab = PLANNER_TABS[brand] || "lacw-aid-planner";
    const page = "Aid Planner";
    items.push(...reminderItems(plannerReminders(planners, { today, availabilityFor }), { brand, tab, page, source: "planner", today }));
    for (const req of openRequisitions(planners)) {
      items.push(item(true, {
        id: `requisition-${brand}-${req.id || req.matterId}`,
        firm: firmLabel(brand),
        text: `ATLAS requisition to answer: ${req.clientName || "Unnamed client"}`,
        detail: req.dueOn ? `Due ${shortDate(parseDay(req.dueOn))}` : "",
        tab,
        page,
        brand,
        focus: "matter",
        targets: req.matterId ? [target(req.matterId, req.clientName || "Unnamed client", req.dueOn ? `Due ${shortDate(parseDay(req.dueOn))}` : "")] : [],
      }));
    }
  }
  return items;
}

export const DIARY_TABS = { vals: "vals-diary", eas: "eas-diary" };

// The diary reminders come from the week the diary opens on, as on the diary page itself.
export function diaryItems(weeksByBrand = {}, today = new Date()) {
  const items = [];
  for (const [brand, rows] of Object.entries(weeksByBrand || {})) {
    const latest = pickLatestWeek(rows);
    const entries = latest ? readWeekArchive(latest.form_data)?.entries || [] : [];
    const tab = DIARY_TABS[brand];
    if (!tab || !entries.length) continue;
    items.push(...reminderItems(diaryReminders(entries, { source: brand, today }), {
      brand, tab, page: `${firmLabel(brand)} Diary`, source: "diary", today,
    }));
  }
  return items;
}

// ---- New file emails --------------------------------------------------------------------

// `requests` are normalised file requests (src/lib/fileRequests.js): status "" is a new one.
export function fileRequestItems(requestsByBrand = {}, today = new Date()) {
  const items = [];
  const backlog = [];
  for (const [brand, requests] of Object.entries(requestsByBrand || {})) {
    const list = Array.isArray(requests) ? requests : [];
    const firm = firmLabel(brand);
    const tab = `${brand}-emails`;
    const page = "New File Emails";
    const fresh = list.filter((request) => !request.status);
    const urgent = fresh.filter((request) => request.urgent);
    const recent = fresh.filter((request) => !request.urgent && (daysSince(request.date, today) ?? Infinity) <= FILE_EMAIL_RECENT_DAYS);
    const older = fresh.length - urgent.length - recent.length;
    const toLodge = list.filter((request) => request.status === "waiting_to_be_submitted_in_atlas");
    if (urgent.length) {
      items.push(item(true, {
        id: `file-urgent-${brand}`,
        firm,
        text: `${plural(urgent.length, "new file email")} marked urgent, not yet actioned`,
        tab,
        page,
        brand,
        focus: "file-request",
        targets: urgent.map((request) => target(
          request.key,
          request.clientName || "Unknown client",
          [`Received ${shortWhen(request.date)}`, request.category].filter(Boolean).join(" · "),
        )),
      }));
    }
    if (recent.length) {
      items.push(item(false, { id: `file-new-${brand}`, firm, text: `${plural(recent.length, "new file email")} from the last 30 days not actioned`, tab, page }));
    }
    if (toLodge.length) {
      items.push(item(false, { id: `file-lodge-${brand}`, firm, text: `${plural(toLodge.length, "file request")} waiting to be submitted in ATLAS`, tab, page }));
    }
    if (older > 0) backlog.push({ firm, count: older, what: "older file email", tab });
  }
  return { items, backlog };
}

// ---- Emailed fee slips and invoices -----------------------------------------------------

export function intakeItems(itemsByBrand = {}, today = new Date()) {
  const items = [];
  const backlog = [];
  for (const [brand, rows] of Object.entries(itemsByBrand || {})) {
    const unsorted = (Array.isArray(rows) ? rows : []).filter((row) => row?.kind === "check_type" && !row.dismissed_at);
    const recent = unsorted.filter((row) => (daysSince(row.received_at || row.created_at, today) ?? Infinity) <= INTAKE_RECENT_DAYS);
    const firm = firmLabel(brand);
    const tab = `${brand}-fee-slip-invoice-payment`;
    if (recent.length) {
      items.push(item(false, {
        id: `intake-${brand}`,
        firm,
        text: `${plural(recent.length, "emailed item")} to sort from the last 7 days`,
        tab,
        page: "Clerk Fee Slips",
      }));
    }
    if (unsorted.length > recent.length) {
      backlog.push({ firm, count: unsorted.length - recent.length, what: "older emailed item", tab });
    }
    // Counsel's fee paid to the firm by VLA: the firm still has to pay the barrister.
    const payCounsel = (Array.isArray(rows) ? rows : []).filter((row) => row?.status === "paid_to_firm" && !row.dismissed_at);
    if (payCounsel.length) {
      items.push(item(true, {
        id: `pay-counsel-${brand}`,
        firm,
        text: `${plural(payCounsel.length, "fee slip")} paid to the firm - pay counsel`,
        detail: `$${payCounsel.reduce((sum, row) => sum + (Number(row.amount) || 0), 0).toFixed(2)}`,
        tab,
        page: "Clerk Fee Slips",
        brand,
        focus: "fee-slip",
        // The Fee Slip page's id for an emailed item (toFeeSlipRow).
        targets: payCounsel.map((row) => target(`email-${row.id}`, emailClientName(row) || "Unnamed client", [row.clerk_name, money(row.amount)].filter(Boolean).join(" · "))),
      }));
    }
  }
  return { items, backlog };
}

// ---- Recover from aid -------------------------------------------------------------------

export function recoupItems(recordsByBrand = {}) {
  const items = [];
  for (const [brand, formData] of Object.entries(recordsByBrand || {})) {
    if (!formData) continue;
    const open = buildRecoupRows(normaliseRecoupData(formData), []).filter((row) => row.stage && row.stage !== "recovered");
    if (!open.length) continue;
    items.push(item(false, {
      id: `recoup-${brand}`,
      firm: firmLabel(brand),
      text: `${plural(open.length, "invoice")} to recover from aid`,
      tab: `${brand}-fee-slip-invoice-payment`,
      page: "Fee Slips",
    }));
  }
  return items;
}

// ---- Disbursements not yet actioned ------------------------------------------------------

// Only disbursements nobody has acted on: still Outstanding, and not marked to be reimbursed.
// Once one is sent to Finance, claimed, paid or given any other status it leaves the summary
// (Emma, 1 Nov 2026; this replaced the urgent "claimed through ATLAS - pay the provider" line
// of 30 Oct).
export function disbursementItems(recordsByBrand = {}) {
  const items = [];
  for (const [brand, formData] of Object.entries(recordsByBrand || {})) {
    if (!formData) continue;
    const fresh = normaliseDisbursementData(formData).items
      .filter((entry) => entry.status === "not_paid" && !entry.reimburse)
      .sort((a, b) => String(b.uploadedAt || b.invoiceDate || "").localeCompare(String(a.uploadedAt || a.invoiceDate || "")));
    if (!fresh.length) continue;
    const total = fresh.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
    items.push(item(false, {
      id: `disbursements-${brand}`,
      firm: firmLabel(brand),
      text: `${plural(fresh.length, "disbursement")} not actioned yet`,
      detail: `$${total.toFixed(2)}`,
      tab: `${brand}-trust-receipts`,
      page: "Disbursements",
      brand,
      focus: "disbursement",
      targets: fresh.map((entry) => target(
        entry.id,
        entry.clientName || "Unnamed client",
        [entry.supplier, money(entry.amount), entry.uploadedAt ? `added ${shortWhen(entry.uploadedAt)}` : ""].filter(Boolean).join(" · "),
      )),
    }));
  }
  return items;
}

// ---- LACW weekly billing ----------------------------------------------------------------

// A week is started once its diary is on the Billing Sheet, which shares it with LACW
// (lacw_aid_week). Every finished week after the last one shared is a week not started.
export function lacwBillingItems(aidWeekRows = [], heldItems = [], today = new Date()) {
  const items = [];
  const days = (Array.isArray(aidWeekRows) ? aidWeekRows : [])
    .flatMap((row) => (row?.form_data?.rows || []).map((entry) => parseDay(entry?.date)))
    .filter((day) => day !== null);
  if (days.length) {
    const todayDay = dayOf(today);
    const weeks = [];
    for (let monday = mondayOf(Math.max(...days)) + 7 * DAY_MS; monday + 4 * DAY_MS < todayDay; monday += 7 * DAY_MS) {
      weeks.push(monday);
    }
    for (const monday of weeks.slice(-MAX_BILLING_WEEKS)) {
      const urgent = billingWeekUrgent(monday, today);
      items.push(item(urgent, {
        id: `lacw-billing-${monday}`,
        firm: "LACW",
        text: `Weekly billing: ${weekRangeLabel(monday)} not started on the Billing Sheet`,
        detail: urgent ? "" : `Turns urgent on ${shortDate(monday + (4 + BILLING_URGENT_AFTER_DAYS) * DAY_MS)}`,
        tab: "billing",
        page: "Billing Sheet",
      }));
    }
  }

  const held = (Array.isArray(heldItems) ? heldItems : []);
  const toClaim = held.filter((entry) => entry?.action === "To be claimed").length;
  const feeSlip = held.filter((entry) => entry?.action === "Counsel appeared" && !entry.feeSlipDoneAt).length;
  const aidPending = held.filter((entry) => entry?.action === "Aid pending").length;
  const parts = [
    toClaim ? `${toClaim} to be claimed` : "",
    feeSlip ? `${feeSlip} awaiting fee slip` : "",
    aidPending ? `${aidPending} aid pending` : "",
  ].filter(Boolean);
  if (parts.length) {
    items.push(item(false, { id: "lacw-held", firm: "LACW", text: `On hold: ${parts.join(", ")}`, tab: "billing", page: "Billing Sheet" }));
  }
  return items;
}

// ---- EAS weekly billing -----------------------------------------------------------------

const WEEKLY_BILLING_SOURCE = "weekly_billing_email";
// Abdul's weekly billing email for a week should be in by the Tuesday after it (Emma).
const EAS_EXPECTED_WEEKDAY = 2;

const needsChecking = (entry) => entry?.aidStatus === "Other" && /check aid/i.test(entry.otherStatusText || "");

// EAS's weekly billing emails become EAS Diary weeks. Lines arrive marked "Check aid".
export function easBillingItems(weekRows = [], today = new Date()) {
  const items = [];
  const weeks = new Map();
  for (const row of Array.isArray(weekRows) ? weekRows : []) {
    const entries = (readWeekArchive(row?.form_data)?.entries || []).filter((entry) => entry?.source === WEEKLY_BILLING_SOURCE);
    for (const entry of entries) {
      const day = parseDay(entry.courtDate);
      if (day === null) continue;
      const monday = mondayOf(day);
      const week = weeks.get(monday) || { toCheck: 0 };
      if (needsChecking(entry)) week.toCheck += 1;
      weeks.set(monday, week);
    }
  }
  // Until the first weekly billing email has been read in, there is nothing to expect.
  if (!weeks.size) return items;

  for (const [monday, week] of [...weeks].sort((a, b) => a[0] - b[0])) {
    if (!week.toCheck) continue;
    const urgent = billingWeekUrgent(monday, today);
    items.push(item(urgent, {
      id: `eas-billing-${monday}`,
      firm: "EAS",
      text: `Weekly billing: ${weekRangeLabel(monday)}, ${plural(week.toCheck, "line")} to check`,
      tab: "eas-diary",
      page: "EAS Diary",
    }));
  }

  const lastMonday = mondayOf(dayOf(today)) - 7 * DAY_MS;
  const weekday = new Date(dayOf(today)).getUTCDay();
  const pastDeadline = weekday === 0 || weekday >= EAS_EXPECTED_WEEKDAY;
  if (pastDeadline && !weeks.has(lastMonday)) {
    items.push(item(false, {
      id: `eas-billing-missing-${lastMonday}`,
      firm: "EAS",
      text: `Weekly billing email for ${weekRangeLabel(lastMonday)} hasn't arrived`,
      detail: "Expected by Tuesday",
      tab: "eas-diary",
      page: "EAS Diary",
    }));
  }
  return items;
}

// ---- Everything -------------------------------------------------------------------------

// `data` holds whatever loaded; a source left undefined failed to load and is named in
// `unavailable`, so a blank list is never mistaken for "nothing to do".
export const SOURCE_NAMES = {
  requests: "Admin inbox",
  certifications: "Certifications",
  matters: "Aid Planner",
  diaryWeeks: "VALS and EAS diaries",
  fileRequests: "New file emails",
  intake: "Emailed fee slips",
  recoup: "Recover from aid",
  disbursements: "Disbursements",
  aidWeeks: "LACW weekly billing",
  heldItems: "LACW held lists",
};

export function buildMorningSummary(data = {}, { today = new Date(), availabilityFor } = {}) {
  const all = [];
  const backlog = [];
  const unavailable = Object.keys(SOURCE_NAMES).filter((key) => data[key] === undefined).map((key) => SOURCE_NAMES[key]);

  if (data.requests !== undefined) all.push(...requestItems(data.requests, today));
  if (data.certifications !== undefined) all.push(...certificationItems(data.certifications, today));
  if (data.matters !== undefined) all.push(...plannerItems(data.matters, { today, availabilityFor }));
  if (data.diaryWeeks !== undefined) {
    all.push(...diaryItems(data.diaryWeeks, today));
    all.push(...easBillingItems(data.diaryWeeks.eas, today));
  }
  if (data.fileRequests !== undefined) {
    const files = fileRequestItems(data.fileRequests, today);
    all.push(...files.items);
    backlog.push(...files.backlog);
  }
  if (data.intake !== undefined) {
    const intake = intakeItems(data.intake, today);
    all.push(...intake.items);
    backlog.push(...intake.backlog);
  }
  if (data.recoup !== undefined) all.push(...recoupItems(data.recoup));
  if (data.disbursements !== undefined) all.push(...disbursementItems(data.disbursements));
  if (data.aidWeeks !== undefined || data.heldItems !== undefined) {
    all.push(...lacwBillingItems(data.aidWeeks || [], data.heldItems || [], today));
  }

  const allClear = [];
  if (data.matters !== undefined && !all.some((entry) => entry.id.startsWith("requisition-"))) allClear.push("ATLAS requisitions");
  if (data.certifications !== undefined && !all.some((entry) => entry.id.startsWith("cert-"))) allClear.push("certifications");
  if (data.requests !== undefined && !all.some((entry) => entry.id.startsWith("inbox-"))) allClear.push("admin inbox");

  return {
    urgent: all.filter((entry) => entry.urgent),
    notUrgent: all.filter((entry) => !entry.urgent),
    backlog,
    allClear,
    unavailable,
  };
}

// "Older backlog: 195 LACW older file emails, ..." in words.
export function backlogText(backlog = []) {
  if (!backlog.length) return "";
  return backlog.map((entry) => `${entry.count} ${entry.firm} ${entry.count === 1 ? entry.what : `${entry.what}s`}`).join(", ");
}
